import { useState } from 'react'
import { Plus, Radio, Inbox } from 'lucide-react'
import type { StreamForIndex, StreamStatus, PaginationInfo } from '@/types/streams'
import { StreamCard } from './StreamCard'
import { Pagination } from '../ui/Pagination'

type FilterStatus = 'all' | StreamStatus

interface StreamsIndexProps {
  streams: StreamForIndex[]
  initialFilter?: string
  pagination?: PaginationInfo
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
  onToggleStatus?: (id: string) => void
  onViewReports?: (id: string) => void
  onUploadCsv?: (id: string) => void
  onViewGems?: (id: string) => void
  onCreate?: () => void
  onFilterChange?: (filter: string) => void
  onPageChange?: (page: number) => void
}

export function StreamsIndex({
  streams,
  initialFilter = 'all',
  pagination,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewReports,
  onUploadCsv,
  onViewGems,
  onCreate,
  onFilterChange,
  onPageChange,
}: StreamsIndexProps) {
  const [filter, setFilter] = useState<FilterStatus>(initialFilter as FilterStatus)

  const handleFilterChange = (newFilter: FilterStatus) => {
    setFilter(newFilter)
    onFilterChange?.(newFilter)
  }

  // Filter streams
  const filteredStreams =
    filter === 'all' ? streams : streams.filter((s) => s.status === filter)

  // Count streams by status
  const activeCount = streams.filter((s) => s.status === 'active').length
  const pausedCount = streams.filter((s) => s.status === 'paused').length

  const filterOptions: { value: FilterStatus; label: string; count: number }[] = [
    { value: 'all', label: 'All', count: streams.length },
    { value: 'active', label: 'Active', count: activeCount },
    { value: 'paused', label: 'Paused', count: pausedCount },
  ]

  // Empty state
  if (streams.length === 0) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
        {/* Header */}
        <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
          <div className="max-w-5xl mx-auto px-6 py-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                  Streams
                </h1>
                <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                  Configure data pipelines for your content sources.
                </p>
              </div>

              <button
                onClick={onCreate}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Stream
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-5xl mx-auto px-6 py-6">
          {/* Empty State */}
          <div className="flex flex-col items-center justify-center py-20">
            {/* Illustration */}
            <div className="relative mb-8">
              {/* Background glow */}
              <div className="absolute inset-0 blur-3xl bg-teal-200/30 dark:bg-teal-900/20 rounded-full scale-150" />

              {/* Icon stack */}
              <div className="relative">
                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-950 dark:to-teal-900 border border-teal-200 dark:border-teal-800 flex items-center justify-center shadow-lg shadow-teal-500/10">
                  <Radio className="w-12 h-12 text-teal-500 dark:text-teal-400" />
                </div>
                {/* Floating elements */}
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-900 border border-amber-200 dark:border-amber-800 flex items-center justify-center shadow-md">
                  <Inbox className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-stone-900 dark:text-stone-100 mb-3 text-center">
              Create your first stream
            </h2>
            <p className="text-stone-500 dark:text-stone-400 max-w-md text-center mb-8 leading-relaxed">
              Streams collect data from email or webhooks and generate AI-powered
              reports with insights and gems.
            </p>

            <button
              onClick={onCreate}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-medium shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all duration-200 hover:-translate-y-0.5"
            >
              <Plus className="w-5 h-5" />
              New Stream
            </button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                Streams
              </h1>
              <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                {filteredStreams.length} of {pagination?.totalCount ?? streams.length} stream{(pagination?.totalCount ?? streams.length) !== 1 ? 's' : ''}
              </p>
            </div>

            <button
              onClick={onCreate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Stream</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>

          {/* Filter Tabs - Now in header */}
          <div className="flex items-center gap-1 mt-5 p-1 bg-stone-100 dark:bg-stone-800 rounded-lg w-fit">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange(option.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  filter === option.value
                    ? 'bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-sm'
                    : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'
                }`}
              >
                {option.label}
                <span
                  className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                    filter === option.value
                      ? 'bg-stone-100 dark:bg-stone-600 text-stone-600 dark:text-stone-300'
                      : 'bg-stone-200/50 dark:bg-stone-700 text-stone-500 dark:text-stone-400'
                  }`}
                >
                  {option.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-6">
        {/* Streams List */}
        {filteredStreams.length > 0 ? (
          <>
            <div className="space-y-4">
              {filteredStreams.map((stream) => (
                <StreamCard
                  key={stream.id}
                  stream={stream}
                  onEdit={() => onEdit?.(stream.id)}
                  onDelete={() => onDelete?.(stream.id)}
                  onToggleStatus={() => onToggleStatus?.(stream.id)}
                  onViewReports={() => onViewReports?.(stream.id)}
                  onUploadCsv={() => onUploadCsv?.(stream.id)}
                  onViewGems={() => onViewGems?.(stream.id)}
                />
              ))}
            </div>

            {pagination && onPageChange && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                perPage={pagination.perPage}
                onPageChange={onPageChange}
              />
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 dark:bg-stone-800 mb-4">
              <Radio className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <p className="text-stone-600 dark:text-stone-400 font-medium">
              No {filter} streams found
            </p>
            <p className="text-sm text-stone-500 dark:text-stone-500 mt-1">
              {filter !== 'all' ? 'Try selecting a different filter' : 'Create a stream to get started'}
            </p>
          </div>
        )}
      </main>
    </div>
  )
}
